import { useRef, useState, useCallback, type JSX, type DragEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@/components';
import { Icon } from '@/components/ui/icon';
import { FormSection } from '@/components/blocks/forms';
import { toast } from '@/lib/toast';
import { blobToDataUrl, compressImageToJpeg } from '@/lib/imageProcessing';
import { cn } from '@/lib/utils';

const MAX_LOGO_BYTES = 512 * 1024;
const LOGO_QUALITY_STEPS = [0.85, 0.75, 0.65, 0.5];
const LOGO_PREVIEW_WIDTH = 220;
const LOGO_PREVIEW_HEIGHT = 140;

function normalizeLogoUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

export function CompanyLogoField({ disabled = false }: { disabled?: boolean }): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { setValue, watch } = useFormContext<Record<string, unknown>>();
  const [hasError, setHasError] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const logoUrl = normalizeLogoUrl(watch('logoUrl'));
  const showFallback = !logoUrl || hasError;

  const resetInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const handleFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        toast.error("Formato non supportato. Seleziona un'immagine.");
        resetInput();
        return;
      }

      try {
        if (file.size <= MAX_LOGO_BYTES) {
          const dataUrl = await blobToDataUrl(file);
          setValue('logoUrl', dataUrl, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setHasError(false);
          toast.success('Logo caricato');
          return;
        }

        const { dataUrl } = await compressImageToJpeg(file, {
          maxBytes: MAX_LOGO_BYTES,
          qualitySteps: LOGO_QUALITY_STEPS,
          fillColor: '#ffffff',
        });
        setValue('logoUrl', dataUrl, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setHasError(false);
        toast.success('Logo caricato');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Impossibile caricare il logo.';
        toast.error(message);
      } finally {
        resetInput();
      }
    },
    [resetInput, setValue]
  );

  const handleRemove = () => {
    setValue('logoUrl', undefined, { shouldDirty: true, shouldValidate: true });
    setHasError(false);
  };

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      if (disabled) return;

      const files = event.dataTransfer?.files;
      if (files?.length) {
        void handleFile(files[0]);
      }
    },
    [disabled, handleFile]
  );

  return (
    <FormSection
      title="Logo aziendale"
      description="PNG o JPG, max 0,5 MB. Trascina un'immagine o clicca per caricarla."
    >
      <div
        className={cn(
          'relative flex flex-col items-center gap-4 rounded-lg border-2 border-dashed p-6 transition-colors',
          isDragOver
            ? 'border-brand-blue-500 bg-brand-blue-50'
            : 'border-[var(--border-default)] bg-neutral-50',
          !disabled && 'cursor-pointer hover:border-brand-blue-400 hover:bg-neutral-100',
          disabled && 'cursor-not-allowed opacity-60'
        )}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(event) => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        aria-label="Carica logo aziendale"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => handleFile(event.target.files?.[0])}
          disabled={disabled}
        />

        {showFallback ? (
          <>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-200">
              <Icon
                name="add_photo_alternate"
                className={cn(
                  'text-3xl',
                  isDragOver ? 'text-brand-blue-600' : 'text-text-tertiary'
                )}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-text-secondary">
                {isDragOver ? 'Rilascia per caricare' : "Trascina un'immagine qui"}
              </p>
              <p className="mt-1 text-xs text-text-tertiary">
                oppure clicca per selezionare un file
              </p>
            </div>
          </>
        ) : (
          <>
            <div
              className="flex items-center justify-center rounded-md border border-[var(--border-default)] bg-surface p-2"
              style={{
                width: LOGO_PREVIEW_WIDTH,
                height: LOGO_PREVIEW_HEIGHT,
              }}
            >
              <img
                src={logoUrl}
                alt="Logo aziendale"
                className="max-h-full max-w-full object-contain"
                onError={() => setHasError(true)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  inputRef.current?.click();
                }}
                disabled={disabled}
              >
                <Icon name="upload_file" className="text-base" />
                Sostituisci
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemove();
                }}
                disabled={disabled}
              >
                Rimuovi
              </Button>
            </div>
          </>
        )}
      </div>
    </FormSection>
  );
}
